import { useCallback, useEffect, useMemo, useState } from 'react';
import { Images } from '../../../../../assets/images/images.index';
import { Price, Property } from '../types/Add-subscription.types';

interface PropertiesEntry {
  id: number;
  count: number;
  type: 'Vehiculos' | 'Motocicleta' | 'Hogar';
  title: string;
  max_property_age: number;
  min_property_age: number;
  properties: Property[];
  alias_name: string;
}

interface UsePropertyDetailsReturn {
  propertyEntries2: PropertiesEntry[];
  handleInputChange: (entryKey: string, field: string, value: string) => void;
  setSelectedBrands: React.Dispatch<
    React.SetStateAction<Record<string, string>>
  >;
  selectedBrands: Record<string, string>;
  getIcon: (type: 'Vehiculos' | 'Motocicleta' | 'Hogar') => string;
}

interface UsePropertyDetailsProps {
  prices: Price[];
  counts: Record<number, number>;
  period: string;
  formData: Record<string, Record<string, string>>;
  setFormData: React.Dispatch<
    React.SetStateAction<Record<string, Record<string, string>>>
  >;
  onValidChange: (isValid: boolean) => void;
}

export const usePropertyDetails = ({
  prices,
  counts,
  period,
  formData,
  setFormData,
  onValidChange,
}: UsePropertyDetailsProps): UsePropertyDetailsReturn => {
  const [selectedBrands, setSelectedBrands] = useState<Record<string, string>>(
    {}
  );

  const getPropertyEntries = useCallback(() => {
    const entries: PropertiesEntry[] = [];

    prices.forEach((price) => {
      if (price.periocity_id.name === period) {
        const count = counts[price.id] || 0;
        if (count > 0) {
          for (let i = 1; i <= count; i++) {
            entries.push({
              id: price.id,
              count: i,
              type: price.product_type_id.name as
                | 'Vehiculos'
                | 'Motocicleta'
                | 'Hogar',
              title: price.title,
              max_property_age: price.max_property_age,
              min_property_age: price.min_property_age,
              properties: price.properties,
              alias_name: price.alias_name,
            });
          }
        }
      }
    });

    return entries;
  }, [counts, prices, period]);

  const propertyEntries = useMemo(getPropertyEntries, [prices, period, counts]);

  useEffect(() => {
    const propertyEntries = getPropertyEntries();
    const newFormData: Record<string, Record<string, string>> = {};
    propertyEntries.forEach((entry) => {
      const key = `${entry.id}-${entry.count}`;
      if (!formData[key]) {
        if (entry.type === 'Hogar') {
          newFormData[key] = {
            direccion: '',
            pais: '',
            direccion2: '',
          };
        } else {
          newFormData[key] = {
            vehicleName: '',
            plateNumber: '',
            brand: '',
            model: '',
            year: '',
            color: '',
          };
        }
      } else {
        newFormData[key] = { ...formData[key] };
        if (newFormData[key].brand && !selectedBrands[key]) {
          setSelectedBrands((prev) => ({
            ...prev,
            [key]: newFormData[key].brand,
          }));
        }
      }
    });

    if (JSON.stringify(newFormData) !== JSON.stringify(formData)) {
      setFormData(newFormData);
    }
  }, [getPropertyEntries, formData, selectedBrands]);

  useEffect(() => {
    const isFormValid = propertyEntries.every((entry) => {
      const formDataForEntry = formData[`${entry.id}-${entry.count}`];
      return entry.properties.every((prop) => {
        if (prop.required) {
          const value = formDataForEntry?.[prop.name];
          return value && value.trim() !== '';
        }
        return true;
      });
    });
    onValidChange(isFormValid);
  }, [formData, propertyEntries, onValidChange]);

  const handleInputChange = (
    entryKey: string,
    field: string,
    value: string
  ) => {
    setFormData((prev) => ({
      ...prev,
      [entryKey]: {
        ...prev[entryKey],
        [field]: value,
      },
    }));
    if (field === 'brand') {
      setSelectedBrands((prev) => ({ ...prev, [entryKey]: value }));
    }
  };

  const getIcon = (type: 'Vehiculos' | 'Motocicleta' | 'Hogar') => {
    const imageKey = type.toLocaleLowerCase() as keyof typeof Images;
    return Images[imageKey];
  };

  const propertyEntries2 = getPropertyEntries();

  return {
    propertyEntries2,
    handleInputChange,
    setSelectedBrands,
    selectedBrands,
    getIcon,
  };
};
